import React, { useMemo, useState } from "react";
import { Grid } from "@mui/material";
import { getIcon } from "utils/iconsConfig";
import { removeFromCart, addToCart } from "store/cartSlice";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import SmallImage from "../SmallImage";
import { getProductIcon } from "assets/productIcon/Products";
import ImageModal from "../ImageModal";
import { findSpecificMenu, findSpecificSubMenu, isAllowed } from "utils/checkUserRigths";
import { usePermissions } from "context/PermissionsContext";

const QuantityCard = ({ designData, removeCart, allData = [], imageModalRightOffset }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  // const [quantity, setQuantity] = useState(designData?.quantity || 1);
  const [imageError, setImageError] = useState(false);
  const [imageModal, setImageModal] = useState({ isOpen: false, imgUrl: null, designNo: null });
  const [currentIndex, setCurrentIndex] = useState(0);

  const permission = usePermissions();

  const hasAccessToSeeWatermark = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Watermark"), "view") && !permission?.isAdmin;
  const hasAccessToYearlySalesData = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Sales By year"), "view");
  const hasAccessToClientSalesData = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Sales By Client"), "view");
  const hasSalesAccess = hasAccessToClientSalesData || hasAccessToYearlySalesData;

  const cartData = useSelector((state) => state.cart.cartData) || [];

  const quantity = useMemo(() => {
    return (
      cartData?.reduce((count, item) => {
        if (item?.styleNumber === designData?.styleNumber) {
          return count + 1;
        }
        return count;
      }, 0) || 0
    );
  }, [cartData, designData?.styleNumber]);

  const textStyles = {
    fontFamily: "Karla",
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "22px",
    letterSpacing: "-0.41px",
  };

  const rowStyle = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontSize: "13px",
    color: "#333",
    minHeight: "24px",
    ...textStyles,
  };

  const handleRemoveCart = () => {
    if (removeCart) {
      removeCart(designData);
    } else {
      dispatch(removeFromCart(designData));
    }

    sessionStorage.setItem("RecentlyRemoved", JSON.stringify(designData));

    window.dispatchEvent(new Event("cartUpdated"));
  };

  const handleQuantityChange = (e) => {
    const newQuantity = parseInt(e.target.value) || 1;
    const currentQuantity = quantity;

    if (newQuantity > currentQuantity) {  
      const diff = newQuantity - currentQuantity;
      for (let i = 0; i < diff; i++) {
        dispatch(
          addToCart({
            location: location,
            product: designData,
            multipleDesigns: true,
          })
        );
      }
    } else if (newQuantity < currentQuantity) { 
      const diff = currentQuantity - newQuantity;
      for (let i = 0; i < diff; i++) {
        dispatch(removeFromCart(designData));
      }
    }
  };

  const parseDiamondInfo = () => {
    if (designData.diamond) {
      return designData.diamond;
    }
    const diaWeight = designData.diaWeight || designData.diaWt || 0;
    const diaPcs = designData.diaPcs || 0;
    return `${diaWeight} Ct / ${diaPcs} Pcs`;
  };

  const parseWeightInfo = () => {
    if (designData.weight) {
      return designData.weight;
    }
    const netWeight = designData.netWeight || designData.netWt || 0;
    const grossWeight = designData.grossWeight || designData.grossWt || 0;
    return `${netWeight} g / ${grossWeight} g`;
  };

  const parseSalesInfo = () => {
    const salesValue = designData?.sales ?? designData?.styleOrderCount ?? designData?.orderCount ?? 0;
    return `${salesValue}`;
  };

  // Get color stone info
  const getColorStoneInfo = () => {
    const weight = designData.colorStoneWeight || 0;
    const pcs = designData.colorStonePcs || 0;
    if (weight > 0 || pcs > 0) {
      return `${weight} Ct / ${pcs} Pcs`;
    }
    return "N/A";
  };

  const resolveIcon = (iconName, color = "black") => {
    const icon = getIcon(iconName, color);

    if (!icon) return null;
    if (React.isValidElement(icon)) {
      if (icon.type === "svg" || (icon.props && icon.props.viewBox)) {
        return React.cloneElement(icon, {
          width: "20",
          height: "20",
          style: {
            width: "20px",
            height: "20px",
            ...icon.props.style,
          },
        });
      }
      return React.cloneElement(icon, {
        style: {
          width: "20px",
          height: "20px",
          fontSize: "20px",
          ...icon.props.style,
        },
        width: "20px",
        height: "20px",
      });
    }
    return (
      <span
        style={{
          fontSize: "20px",
          lineHeight: 1,
          display: "inline-block",
        }}
      >
        {icon}
      </span>
    );
  };

  const handleOpenImageModal = React.useCallback(() => {
    const newIndex = allData?.findIndex((item) => item?.styleNumber === designData?.styleNumber);
    setCurrentIndex(newIndex >= 0 ? newIndex : 0);
    setImageModal({
      isOpen: true,
      imgUrl: designData?.imgUrl || designData?.Orgimg || designData?.imageUrl || designData?.img || null,
      designNo: designData?.styleNumber,
    });
  }, [allData, designData]);

  const handleCloseImageModal = React.useCallback((e) => {
    e?.stopPropagation?.();
    setImageModal({ isOpen: false, imgUrl: null, designNo: null });
  }, []);

  const handleNextClick = () => {
    if (currentIndex < allData?.length - 1) {
      const newIndex = currentIndex + 1;
      const nextItem = allData[newIndex];
      setCurrentIndex(newIndex);
      setImageModal({
        isOpen: true,
        imgUrl: nextItem?.imgUrl || nextItem?.Orgimg || null,
        designNo: nextItem?.styleNumber,
      });
    }
  };

  const handlePrevClick = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      const prevItem = allData[newIndex];
      setCurrentIndex(newIndex);
      setImageModal({
        isOpen: true,
        imgUrl: prevItem?.imgUrl || prevItem?.Orgimg || null,
        designNo: prevItem?.styleNumber,
      });
    }
  };

  const isFirstItem = currentIndex === 0;
  const isLastItem = currentIndex === allData?.length - 1 || allData?.length === 0;

  if (!designData) return null;

  return (
    <Grid item xs={12}>
      <div
        style={{
          width: "100%",
          background: "#fff",
          borderRadius: "8px",
          display: "flex",
          overflow: "hidden",
          transition: "box-shadow 0.2s ease",
        }}
      >
        {/* LEFT SECTION */}
        <div
          style={{
            flex: 1,
            padding: "14px",
            display: "flex",
            flexDirection: "column",
            gap: "3px",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "2px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: 500,
                fontSize: "15px",
                color: "#1f2937",
              }}
            >
              {getProductIcon(designData?.designClass)}
              <span>{designData.styleNumber || designData.id}</span>
            </div>

            <div
              onClick={handleRemoveCart}
              style={{
                width: "20px",
                height: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              {resolveIcon("DeleteIcon")}
            </div>
          </div>

          {/* Diamond */}
          <div style={rowStyle}>
            <div
              style={{
                width: "20px",
                height: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {resolveIcon("diamondCartOrder", "black")}
            </div>
            <span style={textStyles}>{parseDiamondInfo()}</span>
          </div>

          {/* Net Weight */}
          <div style={rowStyle}>
            <div
              style={{
                width: "20px",
                height: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {resolveIcon("goldbar", "black")}
            </div>
            <span style={textStyles}>{parseWeightInfo()}</span>
          </div>

          {/* Sales */}
          <div style={rowStyle}>
            <div
              style={{
                width: "20px",
                height: "20px",
                minWidth: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {hasSalesAccess ? resolveIcon("sales", "black") : null}
            </div>
            <span style={textStyles}>{parseSalesInfo()}</span>
          </div>

          <div style={rowStyle}>
            <div
              style={{
                width: "20px",
                height: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {resolveIcon("blackScaleCartOrder", "black")}
            </div>
            <span style={textStyles}>{designData?.itemSize || "N/A"}</span>
          </div>

          {/* Quantity */}
          <div
            style={{
              ...rowStyle,
              justifyContent: "space-between",
              marginTop: "4px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div
                style={{
                  width: "20px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span style={{ fontSize: "16px" }}>{resolveIcon("QuantityIcon")}</span>
              </div>
              <input
                type="number"
                value={quantity}
                min={1}
                onChange={handleQuantityChange}
                style={{
                  width: "60px",
                  height: "32px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  textAlign: "center",
                  fontSize: "14px",
                  fontFamily: "Karla",
                  fontWeight: 500,
                  lineHeight: "22px",
                  letterSpacing: "-0.41px",
                  padding: "0 8px",
                  outline: "none",
                  transition: "border-color 0.2s ease",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#1976d2")}
                onBlur={(e) => (e.target.style.borderColor = "#ddd")}
              />
            </div>
          </div>
        </div>

        {/* RIGHT IMAGE */}
        <div
          style={{
            width: "200px",
            borderLeft: "1px solid #f0f0f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#fafafa",
          }}
        >
          <div onClick={handleOpenImageModal} style={{ width: "100%", height: "100%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <SmallImage data={designData} objectFit="contain" handleOpenImageModal={handleOpenImageModal} />
          </div>
        </div>
      </div>
      <ImageModal
        open={imageModal.isOpen}
        handleClose={handleCloseImageModal}
        data={imageModal}
        hasAccessToSeeWatermark={hasAccessToSeeWatermark}
        nextClick={() => handleNextClick()}
        prevClick={() => handlePrevClick()}
        hasNext={!isLastItem}
        hasPrev={!isFirstItem}
        selectedCartProducts={allData?.[currentIndex] ?? null}
        rightOffset={imageModalRightOffset}
      />
    </Grid>
  );
};

export default QuantityCard;
